// src/components/screens/ScanHistory.js
import {
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  Eye,
  Link as LinkIcon,
  MapPin,
  RefreshCw,
  Search,
  Shield,
  ShieldCheck,
  Store,
  User,
  XCircle
} from 'lucide-react';
import { useEffect, useState } from 'react';
import StatCard from '../common/StatCard';
import apiService from '../../services/apiService';

const ScanHistory = () => {
  const [scanHistory, setScanHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [officerFilter, setOfficerFilter] = useState('all');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedScan, setSelectedScan] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Transform API data to component format
  const transformScanData = (apiLogs) => {
    return apiLogs.map(log => ({
      id: log._id,
      officerName: log.scannedBy ? `${log.scannedBy.firstName || ''} ${log.scannedBy.lastName || ''}`.trim() || 'Unknown Officer' : 'Unknown Officer',
      officerCode: log.scannedBy?.accessCode || log.scannedBy?.mobile || 'N/A',
      officerEmail: log.scannedBy?.email || 'N/A',
      scanType: log.scanType === 'PHOTO' ? 'Photo' : (log.code?.startsWith('QR') ? 'QR Code' : 'Barcode'),
      rawScanType: log.scanType || 'BARCODE',
      hasPhoto: !!log.hasPhoto,
      location: log.meta?.location ?
        `${log.meta.location.lat}, ${log.meta.location.lng}` :
        log.meta?.locationName || 'Unknown Location',
      data: log.scanType === 'PHOTO' ? '(photo)' : (log.code || 'N/A'),
      status: log.result === 'SUCCESS' ? 'success' : 'failed',
      deviceId: log.meta?.deviceId || 'N/A',
      timestamp: formatTimestamp(log.createdAt),
      rawTimestamp: log.createdAt,
      isVerified: log.isVerified,
      verifiedBy: log.verifiedBy,
      rejectedBy: log.rejectedBy,
      rejectionReason: log.rejectionReason,
      pendingReview: log.pendingReview,
      matchConfidence: log.matchConfidence,
      lowDistinctiveness: log.lowDistinctiveness,
      errorMessage: log.result === 'FAILED' ? (log.rejectionReason || 'Scan failed') : null,
      mobile: log.mobile,
      updatedAt: log.updatedAt
    }));
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Fetch scan history from API
  const fetchScanHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('ScanHistory: Fetching scans...');
      const scansResponse = await apiService.getScans();
      console.log('ScanHistory: Scans response:', scansResponse);

      if (scansResponse && scansResponse.success && scansResponse.logs) {
        const transformedData = transformScanData(scansResponse.logs);
        setScanHistory(transformedData);
        console.log('ScanHistory: Transformed data:', transformedData);
      } else {
        throw new Error('Failed to fetch scan data');
      }

    } catch (error) {
      console.error('ScanHistory: Error fetching data:', error);
      setError(error.message);
      setScanHistory([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Load scan history on component mount
  useEffect(() => {
    fetchScanHistory();
  }, []);

  // Get unique values for filters
  const uniqueLocations = [...new Set(scanHistory.map(scan => scan.location))];
  const uniqueOfficers = [...new Set(scanHistory.map(scan => scan.officerName))];

  // Filter and sort scan history
  useEffect(() => {
    let filtered = [...scanHistory];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(scan =>
        scan.officerName.toLowerCase().includes(searchLower) ||
        scan.officerCode.toLowerCase().includes(searchLower) ||
        scan.location.toLowerCase().includes(searchLower) ||
        scan.data.toLowerCase().includes(searchLower) ||
        scan.deviceId.toLowerCase().includes(searchLower) ||
        scan.mobile.toLowerCase().includes(searchLower)
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(scan => scan.scanType === typeFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(scan => scan.status === statusFilter);
    }

    // Location filter
    if (locationFilter !== 'all') {
      filtered = filtered.filter(scan => scan.location === locationFilter);
    }

    // Officer filter
    if (officerFilter !== 'all') {
      filtered = filtered.filter(scan => scan.officerName === officerFilter);
    }

    // Date filter
    if (dateFilter) {
      filtered = filtered.filter(scan => {
        if (!scan.rawTimestamp) return false;
        const scanDate = new Date(scan.rawTimestamp).toISOString().split('T')[0];
        return scanDate === dateFilter;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'timestamp':
          aValue = new Date(a.rawTimestamp || 0);
          bValue = new Date(b.rawTimestamp || 0);
          break;
        case 'officer':
          aValue = a.officerName.toLowerCase();
          bValue = b.officerName.toLowerCase();
          break;
        case 'location':
          aValue = a.location.toLowerCase();
          bValue = b.location.toLowerCase();
          break;
        case 'type':
          aValue = a.scanType.toLowerCase();
          bValue = b.scanType.toLowerCase();
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredHistory(filtered);
  }, [scanHistory, searchTerm, typeFilter, statusFilter, locationFilter, dateFilter, officerFilter, sortBy, sortOrder]);

  // Refresh data
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchScanHistory();
    } catch (error) {
      console.error('Failed to refresh data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Export to CSV
  const handleExport = () => {
    const csvData = [
      ['Timestamp', 'Officer', 'Officer Code', 'Email', 'Mobile', 'Scan Type', 'Location', 'Data', 'Status', 'Device ID', 'Verified', 'Error Message'],
      ...filteredHistory.map(scan => [
        scan.timestamp,
        scan.officerName,
        scan.officerCode,
        scan.officerEmail,
        scan.mobile,
        scan.scanType,
        scan.location,
        scan.data,
        scan.status,
        scan.deviceId,
        scan.isVerified ? 'Yes' : 'No',
        scan.errorMessage || ''
      ])
    ];

    const csvContent = csvData.map(row => 
      row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scan_history_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // View scan details
  const viewScanDetails = (scan) => {
    setSelectedScan(scan);
    setShowDetailsModal(true);
  };

  // Get status icon and color
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'success':
        return {
          icon: CheckCircle,
          className: 'text-emerald-600 dark:text-emerald-400',
          bgClass: 'bg-emerald-100 dark:bg-emerald-900/50',
          textClass: 'text-emerald-800 dark:text-emerald-300'
        };
      case 'failed':
        return {
          icon: XCircle,
          className: 'text-red-600 dark:text-red-400',
          bgClass: 'bg-red-100 dark:bg-red-900/50',
          textClass: 'text-red-800 dark:text-red-300'
        };
      default:
        return {
          icon: AlertCircle,
          className: 'text-gray-600 dark:text-gray-400',
          bgClass: 'bg-gray-100 dark:bg-gray-700',
          textClass: 'text-gray-800 dark:text-gray-300'
        };
    }
  };

  // Get scan type color
  const getScanTypeColor = (type) => {
    if (type === 'QR Code') return 'bg-accent-100 text-accent-800 dark:bg-accent-900/50 dark:text-accent-300';
    if (type === 'Photo') return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
    return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
  };

  // Shows the full chain for one physical object: its Officer registration
  // plus every verification attempt matched against it (Dealer, Customer,
  // repeat scans), in one chronological timeline — instead of each attempt
  // reading as an unrelated, disconnected log row.
  const [chainModal, setChainModal] = useState(null); // { registration, registrationPhotoUrl, verifications: [{ log, photoUrl }] }
  const [chainLoading, setChainLoading] = useState(false);

  const openChain = async (scan) => {
    setChainLoading(true);
    try {
      const data = await apiService.getScanLogChain(scan.id);
      const registration = data.registration;
      const verifications = data.verifications || [];

      const registrationPhotoUrl = registration.hasPhoto
        ? await apiService.fetchScanPhotoBlob(registration._id).catch(() => null)
        : null;

      const verificationEntries = await Promise.all(
        verifications.map(async (v) => ({
          log: v,
          photoUrl: v.hasPhoto ? await apiService.fetchScanPhotoBlob(v._id).catch(() => null) : null,
        }))
      );

      setChainModal({ registration, registrationPhotoUrl, verifications: verificationEntries });
    } catch (err) {
      alert(`Failed to load chain: ${err.message}`);
    } finally {
      setChainLoading(false);
    }
  };

  const closeChain = () => {
    if (chainModal?.registrationPhotoUrl) URL.revokeObjectURL(chainModal.registrationPhotoUrl);
    chainModal?.verifications?.forEach((v) => v.photoUrl && URL.revokeObjectURL(v.photoUrl));
    setChainModal(null);
  };

  // Rejection (a human override) always wins; a pendingReview log's own
  // isVerified only becomes true once a Super Admin approves it via the
  // separate verify endpoint (pendingReview itself is never cleared by
  // that, so isVerified has to be checked ahead of it to catch approval).
  // A plain SUCCESS/non-pending attempt is a genuine verified match on its
  // own merits — the backend never sets isVerified/verifiedBy on the
  // attempt log itself for that path (only on the Officer's registration
  // document), so this does NOT gate on log.isVerified.
  const chainEntryStatus = (log) => {
    if (log.rejectedBy) return { label: 'Rejected', className: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' };
    if (log.isDuplicate) return { label: 'Duplicate', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' };
    if (log.pendingReview) {
      return log.isVerified
        ? { label: 'Verified (Reviewed)', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' }
        : { label: `Needs Review${typeof log.matchConfidence === 'number' ? ` — ${Math.round(log.matchConfidence * 100)}%` : ''}`, className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' };
    }
    if (log.result === 'SUCCESS') return { label: 'Verified', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' };
    return { label: 'Failed', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' };
  };

  const isChainEntryVerified = (log) => !log.rejectedBy && !log.isDuplicate && (log.isVerified || (log.result === 'SUCCESS' && !log.pendingReview));

  // Officer / Dealer / Customer each get a distinct icon + color so the
  // chain reads as three clearly different roles at a glance, not just
  // repeated generic "verification" rows.
  const ROLE_META = {
    Officer: { label: 'Officer', icon: Shield, badgeClass: 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300', ringClass: 'border-primary-300 dark:border-primary-700', dotClass: 'bg-primary-500' },
    Dealer: { label: 'Dealer', icon: Store, badgeClass: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300', ringClass: 'border-purple-300 dark:border-purple-700', dotClass: 'bg-purple-500' },
    User: { label: 'Customer', icon: User, badgeClass: 'bg-accent-100 text-accent-700 dark:bg-accent-900/50 dark:text-accent-300', ringClass: 'border-accent-300 dark:border-accent-700', dotClass: 'bg-accent-500' },
  };
  const getRoleMeta = (role) => ROLE_META[role] || { label: role || 'Unknown', icon: User, badgeClass: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', ringClass: 'border-gray-300 dark:border-gray-700', dotClass: 'bg-gray-400' };

  // Top-of-modal summary: where is this specific object in the required
  // Officer → Dealer → Customer chain right now, in one glance.
  const getChainSummary = (modal) => {
    const dealerDone = modal.verifications.some(v => v.log.scannedBy?.role === 'Dealer' && isChainEntryVerified(v.log));
    const customerDone = modal.verifications.some(v => v.log.scannedBy?.role === 'User' && isChainEntryVerified(v.log));
    const anyReview = modal.verifications.some(v => v.log.pendingReview && !v.log.isVerified);
    if (customerDone) return { label: 'Fully Verified', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300', icon: ShieldCheck };
    if (anyReview) return { label: 'Needs Review', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300', icon: AlertCircle };
    if (dealerDone) return { label: 'Awaiting Customer', className: 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300', icon: Clock };
    return { label: 'Awaiting Dealer', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', icon: Clock };
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="skeleton h-8 w-56"></div>
            <div className="skeleton h-4 w-72"></div>
          </div>
          <div className="skeleton h-10 w-28 rounded-xl"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[0, 1, 2, 3].map((i) => (
            <StatCard key={i} loading />
          ))}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 space-y-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="skeleton w-10 h-10 rounded-xl flex-shrink-0"></div>
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-1/4"></div>
                <div className="skeleton h-3 w-1/3"></div>
              </div>
              <div className="skeleton h-6 w-20 rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">Error loading scan history: {error}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
            Scan History
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Monitor all scanning activities and system logs ({scanHistory.length} total)
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="group px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 transition-all duration-200 disabled:opacity-50 hover:scale-[1.03] active:scale-[0.98]"
          >
            <RefreshCw className={`h-4 w-4 transition-transform duration-500 ${isRefreshing ? 'animate-spin' : 'group-hover:rotate-180'}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
          >
            <Download className="h-4 w-4" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Scans"
          value={scanHistory.length}
          change={`${uniqueOfficers.length} officer${uniqueOfficers.length === 1 ? '' : 's'}`}
          icon={Activity}
          color="blue"
          trend="neutral"
          delay={0}
        />
        <StatCard
          title="Successful"
          value={scanHistory.filter(s => s.status === 'success').length}
          change={scanHistory.length ? `${Math.round((scanHistory.filter(s => s.status === 'success').length / scanHistory.length) * 100)}% success rate` : 'No scans yet'}
          icon={CheckCircle}
          color="green"
          delay={60}
        />
        <StatCard
          title="Failed"
          value={scanHistory.filter(s => s.status === 'failed').length}
          change={scanHistory.length ? `${Math.round((scanHistory.filter(s => s.status === 'failed').length / scanHistory.length) * 100)}% of total` : 'No scans yet'}
          icon={XCircle}
          color="red"
          trend="down"
          delay={120}
        />
        <StatCard
          title="Locations"
          value={uniqueLocations.length}
          change="Distinct scan sites"
          icon={MapPin}
          color="purple"
          trend="neutral"
          delay={180}
        />
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-sm">
        <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-900/50">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 flex-1">
              {/* Search */}
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search scans, officers, data..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 transition-all text-gray-900 dark:text-white placeholder-gray-500"
                />
              </div>

              {/* Filters Row */}
              <div className="flex flex-wrap gap-3">
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
                />
                
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
                >
                  <option value="all">All Types</option>
                  <option value="QR Code">QR Code</option>
                  <option value="Barcode">Barcode</option>
                  <option value="Photo">Photo</option>
                </select>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
                >
                  <option value="all">All Status</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                </select>

                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
                >
                  <option value="all">All Locations</option>
                  {uniqueLocations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>

                <select
                  value={officerFilter}
                  onChange={(e) => setOfficerFilter(e.target.value)}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
                >
                  <option value="all">All Officers</option>
                  {uniqueOfficers.map(officer => (
                    <option key={officer} value={officer}>{officer}</option>
                  ))}
                </select>

                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [newSortBy, newSortOrder] = e.target.value.split('-');
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                  }}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
                >
                  <option value="timestamp-desc">Latest First</option>
                  <option value="timestamp-asc">Oldest First</option>
                  <option value="officer-asc">Officer A-Z</option>
                  <option value="location-asc">Location A-Z</option>
                  <option value="type-asc">Type A-Z</option>
                </select>
              </div>
            </div>
            
            <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
              Showing {filteredHistory.length} of {scanHistory.length} scans
            </div>
          </div>
        </div>

        {/* Scan History Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200/50 dark:border-gray-600/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Officer</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Scan Details</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Location</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Timestamp</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Verified By</th>
                <th className="sticky right-0 z-10 px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.08)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
              {filteredHistory.length > 0 ? (
                filteredHistory.map((scan, rowIndex) => {
                  const statusDisplay = getStatusDisplay(scan.status);
                  const StatusIcon = statusDisplay.icon;

                  return (
                    <tr
                      key={scan.id}
                      style={{ '--stagger': rowIndex }}
                      className="hover:bg-gray-50/50 dark:hover:bg-gray-700/25 transition-all duration-200 stagger-in"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-600 rounded-xl flex items-center justify-center transition-transform duration-200 hover:scale-105">
                            <User className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white text-sm">
                              {scan.officerName}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                              {scan.officerCode}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getScanTypeColor(scan.scanType)}`}>
                            {scan.scanType}
                          </span>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded max-w-xs truncate">
                            {scan.data}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900 dark:text-white">
                          <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                          <span className="max-w-xs truncate">{scan.location}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900 dark:text-white">
                          <Clock className="h-4 w-4 mr-2 text-gray-400" />
                          <div>
                            <div>{scan.timestamp.split(' ')[0]}</div>
                            <div className="text-xs text-gray-500">{scan.timestamp.split(' ')[1]}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <StatusIcon className={`h-4 w-4 ${statusDisplay.className}`} />
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusDisplay.bgClass} ${statusDisplay.textClass}`}>
                            {scan.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {scan.isVerified ? (
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <div>
                                <div className="font-medium">
                                  {scan.verifiedBy ? `${scan.verifiedBy.firstName || ''} ${scan.verifiedBy.lastName || ''}`.trim() || scan.verifiedBy.email : 'System'}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Verified
                                </div>
                              </div>
                            </div>
                          ) : scan.rejectedBy ? (
                            <div className="flex items-center space-x-2">
                              <XCircle className="h-4 w-4 text-red-500" />
                              <div>
                                <div className="font-medium text-red-600 dark:text-red-400">
                                  Rejected
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {typeof scan.rejectedBy === 'string' ? scan.rejectedBy : `${scan.rejectedBy.firstName || ''} ${scan.rejectedBy.lastName || ''}`.trim() || scan.rejectedBy.email || 'System'}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <Clock className="h-4 w-4 text-yellow-500 animate-pulse-soft" />
                              <div>
                                <div className="font-medium text-yellow-600 dark:text-yellow-400">
                                  Pending
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Awaiting verification
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="sticky right-0 z-10 px-6 py-4 whitespace-nowrap bg-white dark:bg-gray-800 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.08)]">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openChain(scan)}
                            className="group px-3 py-2 rounded-lg bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/30 dark:hover:bg-primary-900/50 text-primary-700 dark:text-primary-300 text-xs font-semibold flex items-center space-x-1.5 transition-all duration-200 hover:scale-[1.03] active:scale-95"
                            title="View this object's full Officer → Dealer → Customer chain"
                          >
                            <LinkIcon className="h-3.5 w-3.5" />
                            <span>Chain</span>
                          </button>
                          <button
                            onClick={() => viewScanDetails(scan)}
                            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-110 active:scale-95"
                            title="View raw scan details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    <div className="flex flex-col items-center space-y-3">
                      <Activity className="h-12 w-12 text-gray-300 dark:text-gray-600" />
                      <div>
                        <p className="text-lg font-medium">No scan history found</p>
                        <p className="text-sm">Try adjusting your search criteria or check back later</p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scan Details Modal */}
      {showDetailsModal && selectedScan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setShowDetailsModal(false)}>
          <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden max-h-[90vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Scan Details
                </h3>
                <button 
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 rounded-xl hover:bg-white/50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white">Scan Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Scan Type</label>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedScan.scanType}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Location</label>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedScan.location}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Device ID</label>
                      <p className="font-medium text-gray-900 dark:text-white font-mono">{selectedScan.deviceId}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Verified</label>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {selectedScan.isVerified ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Officer Info */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white">Officer Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Officer Name</label>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedScan.officerName}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Officer Code</label>
                      <p className="font-medium text-gray-900 dark:text-white font-mono">{selectedScan.officerCode}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Email</label>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedScan.officerEmail}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Mobile</label>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedScan.mobile}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timestamp */}
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">Timestamp</label>
                <p className="font-medium text-gray-900 dark:text-white text-lg">{selectedScan.timestamp}</p>
              </div>

              {/* Scanned Data */}
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">Scanned Data</label>
                <div className="mt-2 p-4 bg-gray-100 dark:bg-gray-700 rounded-xl">
                  <code className="text-sm font-mono text-gray-900 dark:text-white break-all">
                    {selectedScan.data}
                  </code>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center space-x-4">
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">Status</label>
                  <div className="flex items-center space-x-2 mt-1">
                    {(() => {
                      const statusDisplay = getStatusDisplay(selectedScan.status);
                      const StatusIcon = statusDisplay.icon;
                      return (
                        <>
                          <StatusIcon className={`h-5 w-5 ${statusDisplay.className}`} />
                          <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusDisplay.bgClass} ${statusDisplay.textClass}`}>
                            {selectedScan.status}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Verification Details */}
              {(selectedScan.verifiedBy || selectedScan.rejectedBy) && (
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedScan.isVerified ? 'Verified By' : 'Rejected By'}
                  </label>
                  <div className="mt-2 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
                    <p className="text-primary-700 dark:text-primary-400">
                      {selectedScan.verifiedBy?.email || selectedScan.rejectedBy || 'System'}
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message (if failed) */}
              {selectedScan.status === 'failed' && selectedScan.errorMessage && (
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">Error Message</label>
                  <div className="mt-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <p className="text-red-700 dark:text-red-400">{selectedScan.errorMessage}</p>
                  </div>
                </div>
              )}

              {/* Additional Metadata */}
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">Last Updated</label>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatTimestamp(selectedScan.updatedAt)}
                </p>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200/50 dark:border-gray-700/50 flex justify-end bg-gray-50/50 dark:bg-gray-900/50">
              <button 
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {chainLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
          <RefreshCw className="h-8 w-8 animate-spin text-white" />
        </div>
      )}

      {chainModal && (() => {
        const summary = getChainSummary(chainModal);
        const SummaryIcon = summary.icon;
        const officerMeta = getRoleMeta('Officer');
        const OfficerIcon = officerMeta.icon;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={closeChain}>
            <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden max-h-[90vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 flex items-center justify-between sticky top-0 z-10">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Object Chain</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Officer registration + {chainModal.verifications.length} verification attempt{chainModal.verifications.length === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`hidden sm:inline-flex items-center space-x-1.5 px-3 py-1.5 text-xs font-semibold rounded-full ${summary.className}`}>
                    <SummaryIcon className="h-3.5 w-3.5" />
                    <span>{summary.label}</span>
                  </span>
                  <button onClick={closeChain} className="p-2 rounded-xl hover:bg-white/50 dark:hover:bg-gray-700/50">
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="sm:hidden px-6 pt-4">
                <span className={`inline-flex items-center space-x-1.5 px-3 py-1.5 text-xs font-semibold rounded-full ${summary.className}`}>
                  <SummaryIcon className="h-3.5 w-3.5" />
                  <span>{summary.label}</span>
                </span>
              </div>

              {/* Vertical timeline: Officer registration at the root, then
                  every Dealer/Customer verification attempt underneath it in
                  chronological order, each with a colored role node. */}
              <div className="p-6">
                <div className="relative pl-10">
                  <div className="absolute left-[15px] top-3 bottom-3 w-0.5 bg-gray-200 dark:bg-gray-700" />

                  {/* Registration — the root of the chain */}
                  <div className="relative mb-5">
                    <div className={`absolute -left-10 top-0 w-8 h-8 rounded-full flex items-center justify-center ring-4 ring-white dark:ring-gray-800 ${officerMeta.dotClass}`}>
                      <OfficerIcon className="h-4 w-4 text-white" />
                    </div>
                    <div className="border-2 border-primary-200 dark:border-primary-800 rounded-xl p-4 space-y-2 bg-primary-50/50 dark:bg-primary-900/10">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className={`inline-flex items-center space-x-1 px-2 py-0.5 text-xs font-semibold rounded-lg ${officerMeta.badgeClass}`}>
                          <OfficerIcon className="h-3 w-3" />
                          <span>Officer — Enrolled</span>
                        </span>
                        {chainModal.registration.lowDistinctiveness && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-lg bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300">
                            Low texture
                          </span>
                        )}
                      </div>
                      {chainModal.registrationPhotoUrl ? (
                        <img src={chainModal.registrationPhotoUrl} alt="Registered object" className="w-full object-contain max-h-64 rounded-lg bg-gray-100 dark:bg-gray-900" />
                      ) : chainModal.registration.code ? (
                        <p className="font-mono text-sm text-gray-700 dark:text-gray-200">{chainModal.registration.code}</p>
                      ) : null}
                      <div className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
                        <p>
                          By {chainModal.registration.scannedBy
                            ? `${chainModal.registration.scannedBy.firstName} ${chainModal.registration.scannedBy.lastName || ''}`
                            : 'Unknown'}
                        </p>
                        <p>{formatTimestamp(chainModal.registration.createdAt)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Every verification attempt against it, chronologically */}
                  {chainModal.verifications.length === 0 ? (
                    <div className="relative">
                      <div className="absolute -left-10 top-0 w-8 h-8 rounded-full flex items-center justify-center ring-4 ring-white dark:ring-gray-800 bg-gray-300 dark:bg-gray-600">
                        <Clock className="h-4 w-4 text-white" />
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No verification attempts yet — awaiting a Dealer scan.</p>
                    </div>
                  ) : (
                    chainModal.verifications.map(({ log, photoUrl }, idx) => {
                      const status = chainEntryStatus(log);
                      const roleMeta = getRoleMeta(log.scannedBy?.role);
                      const RoleIcon = roleMeta.icon;
                      return (
                        <div key={log._id} className={`relative ${idx < chainModal.verifications.length - 1 ? 'mb-5' : ''}`}>
                          <div className={`absolute -left-10 top-0 w-8 h-8 rounded-full flex items-center justify-center ring-4 ring-white dark:ring-gray-800 ${roleMeta.dotClass}`}>
                            <RoleIcon className="h-4 w-4 text-white" />
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <span className={`inline-flex items-center space-x-1 px-2 py-0.5 text-xs font-semibold rounded-lg ${roleMeta.badgeClass}`}>
                                <RoleIcon className="h-3 w-3" />
                                <span>{roleMeta.label} — Verification</span>
                              </span>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-lg ${status.className}`}>{status.label}</span>
                            </div>
                            {photoUrl && (
                              <img src={photoUrl} alt="Verification attempt" className="w-full object-contain max-h-64 rounded-lg bg-gray-100 dark:bg-gray-900" />
                            )}
                            <div className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
                              <p>
                                By {log.scannedBy?.firstName ? `${log.scannedBy.firstName} ${log.scannedBy.lastName || ''}` : log.mobile}
                              </p>
                              <p>{formatTimestamp(log.createdAt)}</p>
                              {typeof log.matchConfidence === 'number' && (
                                <p>Match confidence: <span className="font-semibold">{Math.round(log.matchConfidence * 100)}%</span></p>
                              )}
                              {log.rejectionReason && <p className="text-red-600 dark:text-red-400">Reason: {log.rejectionReason}</p>}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      )}
    </div>
  );
};

export default ScanHistory;